import { Component, Input, OnInit } from '@angular/core';
import { ApiRoutes } from '@sb/library/api';
import { DesignBlockBaseViewComponent, OrderBy, PaginatorData, SortColumn, SortDirection, WebsiteMode } from '@sb/library/base';
import {
    DisplayItemCollection,
    PublicReviewDisplayItem,
    ReviewDisplayItem,
    ReviewSearchOptions,
    ReviewSharingOption,
    ReviewStatus,
    ReviewSummaryDisplayItem,
    WebsiteBlockReviewsDisplayItem,
    WebsiteBlockReviewsType,
    WebsiteRenderMode
} from '@sb/library/data-models';
import { forkJoin, Observable } from 'rxjs';
import { DesignBlockIframeBaseViewComponent } from '../design-block-iframe-base-view.component';

@Component({
    selector: 'lib-reviews-design-block',
    templateUrl: './reviews-design-block.component.html',
    styleUrls: ['./reviews-design-block.component.scss']
})
export class ReviewsDesignBlockComponent extends DesignBlockIframeBaseViewComponent implements DesignBlockBaseViewComponent, OnInit {
    private _loading: boolean = true;

    // private variables for the block
    private _publicReviews: PublicReviewDisplayItem[] = [];
    private _visibleReviews: PublicReviewDisplayItem[] = []; // the ones being shown on the screen depending on show more/show less
    private _reviewSummary: ReviewSummaryDisplayItem | null = null;
    private _showMoreState: boolean = false;
    private _gridColumns: string = '';
    private _maxColPerRow: number = 0;

    // All preview data from API
    private _allReviewsLength: number | null = null; // determines empty state
    private _allSummaries: ReviewSummaryDisplayItem[] = [];
    private _allReviews: ReviewDisplayItem[] = [];

    private _websiteBlockData!: WebsiteBlockReviewsDisplayItem;

    @Input()
    set websiteBlockData(value: WebsiteBlockReviewsDisplayItem) {
        this._websiteBlockData = value;
        if (this.Scope.websiteMode == WebsiteMode.Preview) {
            this.PreparePreviewData();
        }
    }

    override ngOnInit(): void {
        super.ngOnInit();
        switch (this.Scope.websiteMode) {
            case WebsiteMode.Live:
                if (this.Scope.renderContext.RenderMode == WebsiteRenderMode.Widget) {
                    // need to wait for device type manager for external widget
                    this._iframeLoaded.subscribe(() => {
                        this.SetUpLiveReviewsBlockData();
                    });
                } else {
                    this.SetUpLiveReviewsBlockData();
                }
                break;
            case WebsiteMode.Preview:
                this._maxColPerRow = this.GetMaxColPerRow();
                this.FetchPreviewData();
                break;
        }
    }

    // fetch public reviews to show for preview. in Website live mode it comes from the RenderContext
    FetchPreviewData(): void {
        this._loading = true;

        // determines kind of empty state to display
        const reviewsTotalCountApi = ApiRoutes.Reviews.Search.Call({
            Body: new ReviewSearchOptions({
                Statuses: [ReviewStatus.ReviewedShared],
                TeacherIDs: []
            }),
            PaginatorData: PaginatorData.TotalCount
        });

        const reviewSummaryApi = ApiRoutes.Reviews.Summary.GetAll.Call({
            Parameter: this.schoolID
        });

        forkJoin({
            TotalSharedReviews: reviewsTotalCountApi,
            Reviews: this.FetchReviews(12),
            Summaries: reviewSummaryApi
        }).subscribe(data => {
            // set all preview data
            this._allReviewsLength = data.TotalSharedReviews.TotalItemCount;
            this._allReviews = data.Reviews.ItemSubset;
            this._allSummaries = data.Summaries;

            this.PreparePreviewData();
            this._loading = false;
        });
    }

    ShowMoreClicked(showMore: boolean): void {
        if (showMore) {
            this._visibleReviews = this._publicReviews;
        } else {
            this._visibleReviews = this._publicReviews.length > this._maxColPerRow ? this._publicReviews.slice(0, this._maxColPerRow) : this._publicReviews;
        }
        this._showMoreState = showMore;
        this.DetectChanges();
    }

    ConvertReviewDisplayItemToPublicReviewDisplayItem(review: ReviewDisplayItem): PublicReviewDisplayItem {
        return new PublicReviewDisplayItem({
            ID: review.ID,
            // even though we have the parentID (as an admin), it should show up as Anonymous for the WebsitePreview
            ParentID: review.SharingOption != ReviewSharingOption.PublicNamed ? null : review.ParentID,
            SchoolID: review.SchoolID,
            TeacherID: review.TeacherID,
            CreatedDate: review.CreatedDate,
            RatingPercentage: review.RatingPercentage,
            ReviewBody: review.ReviewBody,
            ParentFirstName: review.SharingOption != ReviewSharingOption.PublicNamed ? null : review.Parent?.FirstName,
            ParentLastName: review.SharingOption != ReviewSharingOption.PublicNamed ? null : review.Parent?.LastName,
            SharingOption: review.SharingOption
        });
    }

    private SetUpLiveReviewsBlockData(): void {
        this._maxColPerRow = this.GetMaxColPerRow();
        this._allReviewsLength = this.websiteBlockData.Reviews.length;
        this._publicReviews = this.PreparePublicReviewsData(this.websiteBlockData.Reviews);
        this.ShowMoreClicked(false);
        this._reviewSummary = this.CalculateReviewSummary(this.websiteBlockData.ReviewSummaries);
        this._gridColumns = this.CalculateGridColumns(this._publicReviews.length);
        this._loading = false;
    }

    private PreparePreviewData(): void {
        this._publicReviews = [];
        // convert to PublicReviewDisplayItem
        this._allReviews.forEach(r => {
            this._publicReviews.push(this.ConvertReviewDisplayItemToPublicReviewDisplayItem(r));
        });
        this._publicReviews = this.PreparePublicReviewsData(this._publicReviews);
        this.ShowMoreClicked(false);
        this._reviewSummary = this.CalculateReviewSummary(this._allSummaries);
        this._gridColumns = this.CalculateGridColumns(this._publicReviews.length);
    }

    private PreparePublicReviewsData(reviews: PublicReviewDisplayItem[]): PublicReviewDisplayItem[] {
        if (this.websiteBlockData.TeacherIDs.length) {
            reviews = reviews.filter(r => this.websiteBlockData.TeacherIDs.includes(r.TeacherID!));
        }
        return reviews.filter(r => r.RatingPercentage >= 80);
    }

    private CalculateReviewSummary(summaries: ReviewSummaryDisplayItem[]): ReviewSummaryDisplayItem {
        let totalRatings = 0;
        let totalScore = 0;

        // prepare from website block data
        if (this.websiteBlockData.TeacherIDs.length) {
            summaries = summaries.filter(s => this.websiteBlockData.TeacherIDs.includes(s.TeacherID!));
        }

        summaries.forEach(summary => {
            totalRatings += summary.TotalRatings;
            totalScore += summary.AverageRating * summary.TotalRatings;
        });

        return new ReviewSummaryDisplayItem({
            AverageRating: Math.abs(totalScore / totalRatings),
            TotalRatings: totalRatings
        });
    }

    private FetchReviews(pageSize: number): Observable<DisplayItemCollection<ReviewDisplayItem>> {
        return ApiRoutes.Reviews.Search.Call({
            Body: new ReviewSearchOptions({
                Statuses: [ReviewStatus.ReviewedShared]
            }),
            PaginatorData: new PaginatorData({
                PageSize: pageSize,
                PageNumber: 0
            }),
            OrderBy: new OrderBy([
                new SortColumn({
                    Column: 'CreatedDate',
                    Direction: SortDirection.Descending
                })
            ])
        });
    }

    // ==== helper methods for CSS and grid calculations based on device screen size ==== 
    private CalculateGridColumns(itemsCount: number): string {
        // Mobile
        if (this.isMobile) {
            return '1fr';
        }

        // Tablet
        if (this.isTablet) {
            return itemsCount >= 2 ? 'repeat(2, 1fr)' : '1fr';
        }

        // Desktop
        if (this.isDesktop) {
            if (itemsCount >= 3) {
                return 'repeat(3, 1fr)';
            } else if (itemsCount == 2) {
                return 'repeat(2, 1fr)';
            }
        }
        return '1fr';
    }

    private GetMaxColPerRow(): number {
        if (this.isMobile) {
            return 1;
        }
        if (this.isTablet) {
            return 2;
        }
        return 3;
    }

    get schoolID(): string {
        return this.websiteBlockData.SchoolID!;
    }

    get kind(): WebsiteBlockReviewsType {
        return this.websiteBlockData.ReviewsType;
    }

    get WebsiteBlockReviewsType(): typeof WebsiteBlockReviewsType {
        return WebsiteBlockReviewsType;
    }

    get allPublicReviewsLength(): number {
        return this._publicReviews.length;
    }

    get visibleReviews(): PublicReviewDisplayItem[] {
        return this._visibleReviews;
    }

    get allReviewsLength(): number | null {
        return this._allReviewsLength;
    }

    get loading(): boolean {
        return this._loading;
    }

    get reviewSummary(): ReviewSummaryDisplayItem | null {
        return this._reviewSummary;
    }

    get showMoreState(): boolean {
        return this._showMoreState;
    }

    get websiteBlockData(): WebsiteBlockReviewsDisplayItem {
        return this._websiteBlockData;
    }

    get gridColumns(): string {
        return this._gridColumns;
    }

    get maxColPerRow(): number {
        return this._maxColPerRow;
    }
}
